import * as React from 'react';
import Card from '@mui/material/Card';
import CardHeader from '@mui/material/CardHeader';
import CardContent from '@mui/material/CardContent';
import CardActions from '@mui/material/CardActions';
import Button from '@mui/material/Button';
import { useInView } from 'react-cool-inview';
import ConditionnalZoomIconButton from 'components/ConditionnalZoomIconButton';
import { styled } from '@mui/material/styles';
import FullScreenProvider from 'contexts/FullScreenContextProvider';
import { BUDGET, INVIEW_DELAY } from 'business/constants';

const StyZoomButtonCardHeaderContainer = styled('div', {
    name: 'StyZoomButtonCardHeaderContainer',
})({
    display: 'flex',
    flexDirection: 'row',
    marginLeft: '10px',
    alignItems: 'center',
});

type BudgetCardProps = {
    title: string;
    subtitle?: string;
    saveButtonTitle?: string;
    children: React.ReactNode;
    exportMenu?: React.ReactNode;
    sx?: {
        width: string;
        margin: string;
    };
    onSubmit?: () => void;
};

/**
 * UI component that decorates a budget component (a table, a pie chart, a bar chart, whatever)
 * by giving him a title, a call to action button and wrapping it in a material ui card.
 */
function BudgetCard({
    title,
    subtitle = '',
    saveButtonTitle = '',
    children,
    exportMenu = null,
    sx = { width: '80%', margin: '1%' },
    onSubmit,
}: BudgetCardProps): React.ReactElement {
    const { observe, inView } = useInView<HTMLDivElement>({
        unobserveOnEnter: true,
        delay: INVIEW_DELAY,
    });
    return (
        <Card
            ref={observe}
            sx={{
                width: sx.width,
                margin: sx.margin,
            }}
            elevation={1}
        >
            <FullScreenProvider module={BUDGET}>
                <StyZoomButtonCardHeaderContainer>
                    <ConditionnalZoomIconButton />
                    <CardHeader
                        action={exportMenu}
                        titleTypographyProps={{ variant: 'body1' }}
                        title={title}
                        subheader={subtitle}
                        sx={{ width: '100%' }}
                    />
                </StyZoomButtonCardHeaderContainer>
                <CardContent>{inView ? children : null}</CardContent>
            </FullScreenProvider>
            <CardActions
                sx={{ justifyContent: 'right', padding: '0 20px 16px 0' }}
            >
                {onSubmit ? (
                    <Button
                        variant="contained"
                        onClick={onSubmit}
                        sx={{ height: '26px', minHeight: '26px' }}
                    >
                        {saveButtonTitle}
                    </Button>
                ) : null}
            </CardActions>
        </Card>
    );
}

export default BudgetCard;
